import { client } from '@/client';
import {
  CampaignMasterTemplate,
  CampaignMasterTemplatesListParams,
  CampaignMasterTemplatesListResponse,
  CreateCampaignMasterTemplateRequest,
  UpdateCampaignMasterTemplateRequest,
} from '@/client/campaigns';
import { GenericForbiddenApiError } from '@/hooks/query';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';

const listQueryKey = 'campaignMasterTemplates';

export const useCampaignMasterTemplates = (
  params?: CampaignMasterTemplatesListParams,
  options: UseQueryOptions = {},
): UseQueryResult<CampaignMasterTemplatesListResponse> & {
  masterTemplates: CampaignMasterTemplatesListResponse | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = options;

  const { data, ...rest } = useQuery(
    [listQueryKey, params],
    () => client.masterTemplates.getMasterTemplates(params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      keepPreviousData: true,
      ...(restOptions as any),
    },
  );

  return {
    masterTemplates: data,
    ...(rest as UseQueryResult<CampaignMasterTemplatesListResponse>),
  };
};

export const useCampaignMasterTemplate = (
  id: string,
  options: UseQueryOptions = {},
): UseQueryResult<CampaignMasterTemplate> & {
  masterTemplate: CampaignMasterTemplate | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = options;

  const { data, ...rest } = useQuery(
    [listQueryKey, id],
    () => client.masterTemplates.getMasterTemplate(id),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      enabled: !!id,
      ...(restOptions as any),
    },
  );

  return {
    masterTemplate: data,
    ...(rest as UseQueryResult<CampaignMasterTemplate>),
  };
};

export const useCreateCampaignMasterTemplate = (): {
  create: (
    payload: CreateCampaignMasterTemplateRequest,
  ) => Promise<CampaignMasterTemplate>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    CampaignMasterTemplate,
    GenericForbiddenApiError,
    CreateCampaignMasterTemplateRequest
  >(
    'createCampaignMasterTemplate',
    (payload) => client.masterTemplates.createMasterTemplate(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([listQueryKey]);
      },
    },
  );

  return { create: mutateAsync, ...(rest as any) };
};

export const useUpdateCampaignMasterTemplate = (): {
  update: (payload: {
    id: string;
    request: UpdateCampaignMasterTemplateRequest;
  }) => Promise<CampaignMasterTemplate>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    CampaignMasterTemplate,
    GenericForbiddenApiError,
    { id: string; request: UpdateCampaignMasterTemplateRequest }
  >(
    'updateCampaignMasterTemplate',
    (payload) =>
      client.masterTemplates.updateMasterTemplate(payload.id, payload.request),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries([listQueryKey]);
        queryClient.invalidateQueries([listQueryKey, id]);
      },
    },
  );

  return { update: mutateAsync, ...(rest as any) };
};

export const useDeleteCampaignMasterTemplate = (): {
  remove: (id: string) => Promise<void>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    void,
    GenericForbiddenApiError,
    string
  >(
    'deleteCampaignMasterTemplate',
    (id) => client.masterTemplates.deleteMasterTemplate(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([listQueryKey]);
      },
    },
  );

  return { remove: mutateAsync, ...(rest as any) };
};

export const useDisassociateCampaignTemplate = (): {
  disassociate: (payload: {
    masterTemplateId: string;
    templateId: string;
  }) => Promise<void>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    void,
    GenericForbiddenApiError,
    { masterTemplateId: string; templateId: string }
  >(
    'disassociateCampaignTemplate',
    ({ masterTemplateId, templateId }) =>
      client.masterTemplates.disassociateTemplate(masterTemplateId, templateId),
    {
      onSuccess: (_, { masterTemplateId }) => {
        queryClient.invalidateQueries([listQueryKey]);
        queryClient.invalidateQueries([listQueryKey, masterTemplateId]);
      },
    },
  );

  return { disassociate: mutateAsync, ...(rest as any) };
};

export const useSetPrimaryTemplate = (): {
  setPrimary: (payload: {
    masterTemplateId: string;
    templateId: string;
  }) => Promise<void>;
  isLoading: boolean;
  error: GenericForbiddenApiError | null;
} => {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    void,
    GenericForbiddenApiError,
    { masterTemplateId: string; templateId: string }
  >(
    'setPrimaryTemplate',
    ({ masterTemplateId, templateId }) =>
      client.masterTemplates.setPrimaryTemplate(masterTemplateId, templateId),
    {
      onSuccess: (_, { masterTemplateId }) => {
        queryClient.invalidateQueries([listQueryKey]);
        queryClient.invalidateQueries([listQueryKey, masterTemplateId]);
      },
    },
  );

  return { setPrimary: mutateAsync, ...(rest as any) };
};
